import type { ReactNode } from 'react';
import { TaskTypeDot } from './TaskTypeDot';
import { TaskTagStripe } from './TaskTagStripe';
import { useTaskTagDisplay } from '../../hooks/useTaskTagDisplay';
import { Link } from 'react-router-dom';
import { MessageSquare, Paperclip, Trash2, FolderOpen, Check } from 'lucide-react';
import { type Task } from '../../hooks/queries/useTasks';
import { useUndoableTaskDelete } from '../../hooks/useUndoableTaskDelete';
import type { CollaboratorSummary } from '../../services/tasks/taskCollaborationService';
import type { TaskTag } from '../../services/tasks/taskTagService';
import { readableTextColor } from '../../lib/colorContrast';
import { AssigneeAvatars } from './AssigneeAvatars';
import { AssigneeLabel } from './AssigneeLabel';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { TaskPriorityBadge, TaskDueBadge } from './TaskInlineBadges';

export interface TaskDossierRef {
  id: string;
  number: string | null;
  title: string;
}

interface TaskCardProps {
  task: Task;
  variant?: 'list' | 'board';
  onOpen?: (task: Task) => void;
  assignees?: CollaboratorSummary[];
  commentCount?: number;
  attachmentCount?: number;
  /** Dossier rattaché — affiché uniquement en vue multi-dossiers (lien + réf). */
  dossierRef?: TaskDossierRef | null;
  /** Affiche le nom du responsable (1er assigné) à côté de l'avatar. */
  showAssigneeName?: boolean;
  /** Tags couleur à afficher sur la carte. */
  tags?: TaskTag[];
  /** Poignée de drag (variant list) — rendue en premier enfant du <li>. */
  dragHandle?: ReactNode;
  /** Ref posée sur le <li> racine (variant list) — pour dnd-kit. */
  containerRef?: (node: HTMLElement | null) => void;
  /** Classes additionnelles sur le <li> racine (variant list) — pour dnd-kit (isOver/isDragging). */
  containerClassName?: string;
  /** Active l'affordance de sélection multiple (Lot 4). */
  selectable?: boolean;
  /** Tâche actuellement sélectionnée. */
  selected?: boolean;
  /** Au moins une tâche sélectionnée quelque part → case toujours visible. */
  selectionActive?: boolean;
  onToggleSelect?: (id: string, shiftKey: boolean) => void;
  /** Survol de la ligne (variant list) — aperçu. */
  onPreview?: (el: HTMLElement) => void;
  onPreviewEnd?: () => void;
}

function SelectBox({
  taskId,
  selected,
  selectionActive,
  onToggleSelect,
  className = '',
}: {
  taskId: string;
  selected: boolean;
  selectionActive: boolean;
  onToggleSelect: (id: string, shiftKey: boolean) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label="Sélectionner la tâche"
      onClick={(e) => {
        e.stopPropagation();
        onToggleSelect(taskId, e.shiftKey);
      }}
      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition ${
        selected
          ? 'bg-cime-primary border-cime-primary opacity-100'
          : `border-cime-border bg-white hover:border-cime-primary ${
              selectionActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`
      } ${className}`}
    >
      {selected && <Check className="w-3 h-3 text-white" />}
    </button>
  );
}

export function TaskCard({
  task,
  variant = 'list',
  onOpen,
  assignees = [],
  commentCount = 0,
  attachmentCount = 0,
  dossierRef = null,
  showAssigneeName = false,
  tags,
  dragHandle,
  containerRef,
  containerClassName = '',
  selectable = false,
  selected = false,
  selectionActive = false,
  onToggleSelect,
  onPreview,
  onPreviewEnd,
}: TaskCardProps) {
  const requestDelete = useUndoableTaskDelete();
  const { tags: showTags } = useEffectiveTaskFields();
  const tagDisplay = useTaskTagDisplay();
  const stripe = showTags && tagDisplay === 'border' && tags && tags.length > 0 ? tags : null;

  function handleDelete() {
    requestDelete({ id: task.id, content: task.content });
  }

  if (variant === 'board') {
    return (
      <article
        className={`group relative bg-white rounded-lg border border-cime-border px-3 py-2.5 shadow-sm hover:shadow transition ${
          onOpen ? 'cursor-pointer' : 'cursor-default'
        } ${selected ? 'ring-1 ring-cime-primary bg-cime-primary/5' : ''}`}
        onClick={(e) => {
          if (selectable && e.shiftKey) {
            onToggleSelect?.(task.id, true);
            return;
          }
          if (!onOpen) return;
          // N'ouvre que si le clic n'est pas sur la checkbox/bouton suppression
          const target = e.target as HTMLElement;
          if (target.closest('button, [role="checkbox"]')) return;
          onOpen(task);
        }}
      >
        {stripe ? <TaskTagStripe tags={stripe} /> : null}
        {selectable && onToggleSelect && (
          <SelectBox
            taskId={task.id}
            selected={selected}
            selectionActive={selectionActive}
            onToggleSelect={onToggleSelect}
            className="absolute top-2 right-2"
          />
        )}
        {/* Terminer une tâche passe par le panneau (clic sur la carte) —
            la case rapide a été retirée : trop de clics accidentels. */}
        <header className="flex items-start gap-2 mb-1.5">
          <h3
            className={`flex-1 text-sm leading-snug font-body! ${
              task.is_completed
                ? 'text-cime-text-muted'
                : 'text-cime-text-primary'
            }`}
          >
            <TaskTypeDot templateId={task.task_template_id} />
            {task.content}
          </h3>
          <button
            type="button"
            onClick={handleDelete}
            className={`opacity-0 group-hover:opacity-100 transition text-neutral-400 hover:text-red-600 mt-0.5 ${
              selectable ? 'mr-5' : ''
            }`}
            aria-label="Supprimer la tâche"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </header>

        {task.description && (
          <p className="text-xs text-cime-text-muted mb-2 line-clamp-2 ml-6">
            {task.description}
          </p>
        )}

        {showTags && tagDisplay === 'chips' && tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2 ml-6">
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="text-[10px] px-1.5 py-0 rounded-full"
                style={{ backgroundColor: tag.color, color: readableTextColor(tag.color) }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        <footer className="flex items-center gap-2 ml-6 text-xs">
          <TaskPriorityBadge task={task} />
          <TaskDueBadge task={task} />
          {commentCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-cime-text-muted">
              <MessageSquare className="w-3 h-3" />
              {commentCount}
            </span>
          )}
          {attachmentCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-cime-text-muted">
              <Paperclip className="w-3 h-3" />
              {attachmentCount}
            </span>
          )}
          {assignees.length > 0 && (
            <span className="ml-auto">
              <AssigneeAvatars assignees={assignees} size="xs" max={3} />
            </span>
          )}
        </footer>
      </article>
    );
  }

  // variant === 'list'
  return (
    <li
      ref={containerRef}
      className={`relative flex items-center gap-3 px-4 py-3 hover:bg-[#fbfaf7] group ${
        onOpen ? 'cursor-pointer' : ''
      } ${selected ? 'bg-cime-primary/5' : ''} ${containerClassName}`}
      onClick={(e) => {
        if (selectable && e.shiftKey) {
          onToggleSelect?.(task.id, true);
          return;
        }
        if (!onOpen) return;
        const target = e.target as HTMLElement;
        if (target.closest('button, [role="checkbox"]')) return;
        onOpen(task);
      }}
      onMouseEnter={(e) => onPreview?.(e.currentTarget)}
      onMouseLeave={() => onPreviewEnd?.()}
    >
      {stripe ? <TaskTagStripe tags={stripe} /> : null}
      {dragHandle}

      {selectable && onToggleSelect && (
        <SelectBox
          taskId={task.id}
          selected={selected}
          selectionActive={selectionActive}
          onToggleSelect={onToggleSelect}
        />
      )}

      <div className="flex-1 min-w-0">
        <span
          className={`block text-sm truncate ${
            task.is_completed
              ? 'text-cime-text-muted'
              : 'text-cime-text-primary'
          }`}
        >
          <TaskTypeDot templateId={task.task_template_id} />
          {task.content}
        </span>
        {dossierRef && (
          <Link
            to={`/dossiers/${dossierRef.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 mt-0.5 text-xs text-cime-text-muted hover:text-cime-primary hover:underline max-w-full"
            title={`${dossierRef.title}${dossierRef.number ? ` — ${dossierRef.number}` : ''}`}
          >
            <FolderOpen className="w-3 h-3 flex-shrink-0" aria-hidden />
            <span className="truncate">{dossierRef.title}</span>
            {dossierRef.number && (
              <span className="text-cime-text-muted/70 flex-shrink-0">
                · {dossierRef.number}
              </span>
            )}
          </Link>
        )}
        {showTags && tagDisplay === 'chips' && tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="text-[10px] px-1.5 py-0 rounded-full"
                style={{ backgroundColor: tag.color, color: readableTextColor(tag.color) }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {showAssigneeName && assignees.length > 0 && (
        <AssigneeLabel assignees={assignees} size="xs" />
      )}

      {commentCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-xs text-cime-text-muted"
          title={`${commentCount} commentaire${commentCount > 1 ? 's' : ''}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {commentCount}
        </span>
      )}
      {attachmentCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-xs text-cime-text-muted"
          title={`${attachmentCount} pièce${attachmentCount > 1 ? 's' : ''} jointe${attachmentCount > 1 ? 's' : ''}`}
        >
          <Paperclip className="w-3.5 h-3.5" />
          {attachmentCount}
        </span>
      )}
      {!showAssigneeName && assignees.length > 0 && (
        <AssigneeAvatars assignees={assignees} size="xs" max={3} />
      )}

      {/* Colonnes à largeur fixe : échéance et priorité s'alignent
          verticalement d'une ligne à l'autre (scan visuel). */}
      <span className="w-24 flex justify-end flex-shrink-0">
        <TaskDueBadge task={task} />
      </span>

      <span className="w-9 flex justify-end flex-shrink-0">
        <TaskPriorityBadge task={task} />
      </span>

      <button
        type="button"
        onClick={handleDelete}
        className="opacity-0 group-hover:opacity-100 transition text-neutral-400 hover:text-red-600"
        aria-label="Supprimer la tâche"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}
